import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { EnhancedAuthProvider } from "@/features/auth/components/enhanced-auth-provider";
import { ProtectedRoute } from "@/features/auth/components/protected-route";
import { OnboardingGuard } from "@/features/auth/components/onboarding-guard";
import React, { Suspense } from "react";
import { Toaster } from "sonner";
import { useAuth } from "@/features/auth/hooks/use-auth";
import { useEnhancedUserProfile } from "@/lib/hooks/use-enhanced-user-profile";
import { useEnhancedOnboardingCheck } from "@/features/auth/hooks/use-enhanced-onboarding-check";

import { SiteHeader } from "./features/sidebar/site-header";
import {
  IconAlertHexagon,
  IconAlertSquare,
  IconInfoCircle,
  IconLoader2,
  IconSquareCheck,
} from "@tabler/icons-react";

// Lazy load components with optimized chunking
const Landing = React.lazy(
  () => import("@/features/landing-page/landing-page")
);

// Auth pages - can be loaded on demand
const Login = React.lazy(() =>
  import("@/features/auth/pages/login").then((module) => ({
    default: module.Login,
  }))
);
const SignUp = React.lazy(() =>
  import("@/features/auth/pages/signup").then((module) => ({
    default: module.SignUp,
  }))
);
const Verify = React.lazy(() =>
  import("@/features/auth/pages/verify").then((module) => ({
    default: module.Verify,
  }))
);

// Profile page - high priority, preload immediately
const Profile = React.lazy(() => {
  const profileImport = import("@/features/profile/pages/profile").then(
    (module) => ({
      default: module.Profile,
    })
  );

  // Preload profile components immediately since most users visit profiles
  import("@/features/profile/components/profile-info");
  import("@/features/profile/components/main-view");
  import("@/shared/components/simple-media-asset");

  return profileImport;
});

// Work page - medium priority
const WorkPage = React.lazy(() =>
  import("@/features/work/pages/work-page").then((module) => ({
    default: module.WorkPage,
  }))
);

// Edit pages - lower priority, load on demand
const EditProfilePage = React.lazy(() =>
  import("@/features/profile/pages/edit-profile").then((module) => ({
    default: module.EditProfilePage,
  }))
);
const EditWorkPage = React.lazy(() =>
  import("@/features/work/pages/edit-work").then((module) => ({
    default: module.EditWorkPage,
  }))
);
const UploadWorkPage = React.lazy(() =>
  import("@/features/work").then((module) => ({
    default: module.UploadWorkPage,
  }))
);

// Premium pages - lower priority
const PremiumSuccessPage = React.lazy(() =>
  import("@/features/premium/pages/premium-success").then((module) => ({
    default: module.PremiumSuccessPage,
  }))
);
const PremiumFailedPage = React.lazy(() =>
  import("@/features/premium/pages/premium-failed").then((module) => ({
    default: module.PremiumFailedPage,
  }))
);
const PricingPage = React.lazy(() =>
  import("@/features/premium/pages").then((module) => ({
    default: module.PricingPage,
  }))
);

// Search page - medium priority
const SearchPage = React.lazy(() =>
  import("@/features/search/pages/search-page").then((module) => ({
    default: module.SearchPage,
  }))
);

// Onboarding - lower priority
const Onboarding = React.lazy(() =>
  import("@/features/onboarding/pages/onboarding").then((module) => ({
    default: module.Onboarding,
  }))
);

// Create a client with optimized settings for the enhanced auth system
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 10, // 10 minutes (increased from 5)
      gcTime: 1000 * 60 * 120, // 2 hours (increased from 1)
      retry: 1,
      refetchOnWindowFocus: false, // Reduce unnecessary refetches
    },
  },
});

// Minimal loading indicator for Suspense fallbacks
function MinimalLoader() {
  return (
    <div className="flex items-center justify-center min-h-[200px]">
      <IconLoader2 size={20} className="animate-spin text-medium/60" />
    </div>
  );
}

// Enhanced Home route component using the new system
function HomeRoute() {
  const { user, loading: authLoading } = useAuth();
  const { profile, isLoading: profileLoading } = useEnhancedUserProfile();
  const { needsOnboarding, loading: onboardingLoading } =
    useEnhancedOnboardingCheck();

  // For logged-out users, show landing page immediately - no loading states needed
  if (!user && !authLoading) {
    return (
      <Suspense fallback={<MinimalLoader />}>
        <Landing />
      </Suspense>
    );
  }

  // If we're still determining auth state, show minimal loading
  if (authLoading) {
    return <MinimalLoader />;
  }

  // From here on, we know user is logged in, so do the auth-related checks

  // Check if user needs onboarding first - this takes priority
  if (user && needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  // If user is logged in and we have their profile data with username, redirect to profile
  if (user && profile?.username) {
    return <Navigate to={`/${profile.username}`} replace />;
  }

  // If user is logged in but profile is still loading, show a minimal loading state
  if (user && profileLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <IconLoader2 size={24} className="animate-spin text-medium" />
      </div>
    );
  }

  // Fallback to landing page (shouldn't normally reach here for logged-in users)
  return (
    <Suspense fallback={<MinimalLoader />}>
      <Landing />
    </Suspense>
  );
}

function AppContent() {
  const { user } = useAuth();
  const location = useLocation();
  const isOnboardingRoute = location.pathname === "/onboarding";

  // Remove global loading - let each page handle its own loading
  // Only the HomeRoute needs to check auth loading for redirects

  return (
    <div className="[--header-height:calc(theme(spacing.14))] h-[100dvh] max-h-[100dvh] overflow-hidden w-screen max-w-full">
      <div className="flex flex-col h-full">
        {!isOnboardingRoute && <SiteHeader />}
        <main className="flex-grow h-full overflow-auto scrollbar-hide">
          <Suspense fallback={<MinimalLoader />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomeRoute />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/access" element={<Verify />} />
              <Route path="/pricing" element={<PricingPage />} />

              {/* Apply onboarding guard to these routes */}
              <Route element={<OnboardingGuard />}>
                <Route path="/:username" element={<Profile />} />
                <Route path="/:username/:workSlug" element={<WorkPage />} />
                <Route path="/search" element={<SearchPage />} />
              </Route>

              {/* Premium Routes */}
              <Route path="/premium/success" element={<PremiumSuccessPage />} />
              <Route path="/premium/failed" element={<PremiumFailedPage />} />

              {/* Onboarding Route */}
              <Route path="/onboarding" element={<Onboarding />} />

              {/* Protected Routes */}
              <Route element={<ProtectedRoute />}>
                <Route element={<OnboardingGuard />}>
                  <Route path=":username/edit" element={<EditProfilePage />} />
                  <Route path="/upload" element={<UploadWorkPage />} />
                  <Route
                    path=":username/:workSlug/edit"
                    element={<EditWorkPage />}
                  />
                </Route>
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </main>
        <Toaster
          toastOptions={{
            classNames: {
              toast: "!rounded-lg py-4 px-6 !shadow-none font-rg",
              info: "!bg-light",
              loading: "!bg-light",
            },
          }}
          position="bottom-right"
          expand={true}
          richColors
          icons={{
            success: <IconSquareCheck size={20} />,
            info: <IconInfoCircle size={20} />,
            warning: <IconAlertSquare size={20} />,
            error: <IconAlertHexagon size={20} />,
            loading: <IconLoader2 size={20} className="animate-spin" />,
          }}
          offset={{ bottom: "12px", right: "24px" }}
        />
      </div>
    </div>
  );
}

function App() {
  // Preload critical chunks immediately when app starts
  React.useEffect(() => {
    // Preload profile components since most users visit profiles
    import("@/features/profile/pages/profile");
    import("@/features/profile/components/profile-info");
    import("@/features/profile/components/main-view");

    // Preload work page since it's commonly accessed from profiles
    import("@/features/work/pages/work-page");

    // Preload search since it's in the header
    import("@/features/search/pages/search-page");
  }, []);

  return (
    <Router>
      <QueryClientProvider client={queryClient}>
        <EnhancedAuthProvider>
          <AppContent />
        </EnhancedAuthProvider>
      </QueryClientProvider>
    </Router>
  );
}

export default App;
